import { adminEditingTopicIdx, set, topicsCache } from '../../state';
import type { Topic } from '../../types';
import { callApi, callGet } from '../api';
import { buildRichEditor, esc, getRichData, toast } from '../ui';
import { normalizeStep } from '../tickets';

export const DEFAULT_TOPICS: Topic[] = [
  { name: 'ลางาน',         steps: [{ name: 'รับเรื่อง',      msg: { text: 'ทีม HR ได้รับคำขอลางานของคุณแล้วครับ', imageUrl: '', links: [] } }, { name: 'ตรวจสอบวันลา', msg: { text: 'กำลังตรวจสอบวันลาคงเหลือ', imageUrl: '', links: [] } }, { name: 'แจ้งผล',  msg: { text: 'การขอลางานได้รับการอนุมัติแล้วครับ', imageUrl: '', links: [] } }] },
  { name: 'เบิกค่าใช้จ่าย', steps: [{ name: 'รับเรื่อง',      msg: { text: 'ทีม HR ได้รับเรื่องเบิกค่าใช้จ่ายแล้วครับ', imageUrl: '', links: [] } }, { name: 'ตรวจสอบเอกสาร', msg: { text: 'กำลังตรวจสอบเอกสาร', imageUrl: '', links: [] } }, { name: 'โอนเงิน', msg: { text: 'ดำเนินการโอนเงินเรียบร้อยแล้วครับ', imageUrl: '', links: [] } }] },
  { name: 'ขอเอกสาร',      steps: [{ name: 'รับเรื่อง',      msg: { text: 'รับเรื่องขอเอกสารแล้วครับ', imageUrl: '', links: [] } }, { name: 'จัดเตรียม', msg: { text: 'กำลังจัดเตรียมเอกสาร', imageUrl: '', links: [] } }, { name: 'จัดส่ง',  msg: { text: 'เอกสารพร้อมแล้วครับ', imageUrl: '', links: [] } }] },
  { name: 'ปัญหาเงินเดือน', steps: [{ name: 'รับเรื่อง',      msg: { text: 'รับเรื่องปัญหาเงินเดือนแล้วครับ', imageUrl: '', links: [] } }, { name: 'ตรวจสอบ', msg: { text: 'กำลังตรวจสอบรายละเอียด', imageUrl: '', links: [] } }, { name: 'แจ้งผล', msg: { text: 'ตรวจสอบเรียบร้อยแล้วครับ', imageUrl: '', links: [] } }] },
  { name: 'อื่นๆ',          steps: [{ name: 'รับเรื่อง',      msg: { text: 'ทีม HR ได้รับเรื่องของคุณแล้วครับ', imageUrl: '', links: [] } }, { name: 'ดำเนินการ', msg: { text: 'กำลังดำเนินการอยู่ครับ', imageUrl: '', links: [] } }, { name: 'แจ้งผล', msg: { text: 'ดำเนินการเรียบร้อยแล้วครับ', imageUrl: '', links: [] } }] },
];

export async function loadTopics(): Promise<void> {
  const cached = localStorage.getItem('hr_topics');
  if (cached) try { set.topicsCache(JSON.parse(cached)); } catch { /* invalid cache */ }
  const [data, colorsData] = await Promise.all([callGet('topics'), callApi({ action: 'getLineConfig' })]);
  const cfg = colorsData.ok ? (colorsData['config'] as Record<string, unknown> | undefined) ?? {} : {};
  const topicColors = (cfg['topicColors'] as Record<string, string>) ?? {};
  if (data.ok && data['topics']) {
    set.topicsCache((data['topics'] as Topic[]).map(t => ({
      ...t,
      lineColor: (topicColors as Record<string, string>)[t.name] ?? t.lineColor ?? '',
    })));
    localStorage.setItem('hr_topics', JSON.stringify(topicsCache));
  } else if (!topicsCache.length) {
    set.topicsCache([...DEFAULT_TOPICS]);
  }
}

export function renderAdminTopicList(): void {
  const el = document.getElementById('adminTopicList');
  if (!el) return;
  el.innerHTML = topicsCache.length ? topicsCache.map((t, i) => `
    <div class="topic-item">
      <div class="topic-item-accent" style="${t.lineColor ? `background:${esc(t.lineColor)}` : ''}"></div>
      <div class="topic-item-body">
        <div class="topic-item-name">
          ${t.lineColor ? `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${esc(t.lineColor)};flex-shrink:0"></span>` : '<i class="ti ti-tag"></i>'}
          ${esc(t.name)}
        </div>
        <div class="topic-item-flow">
          ${t.steps.map((s, si) => { const ns = normalizeStep(s); return `
            <div class="step-node">
              <div class="step-node-num">${si + 1}</div>
              <span class="step-node-label">${esc(ns.name)}</span>
            </div>
            ${si < t.steps.length - 1 ? '<span class="step-arrow">›</span>' : ''}
          `; }).join('')}
        </div>
      </div>
      <div class="topic-item-actions">
        <button class="icon-btn" onclick="moveAdminTopic(${i},-1)" ${i === 0 ? 'disabled' : ''} title="เลื่อนขึ้น"><i class="ti ti-chevron-up"></i></button>
        <button class="icon-btn" onclick="moveAdminTopic(${i},1)" ${i === topicsCache.length - 1 ? 'disabled' : ''} title="เลื่อนลง"><i class="ti ti-chevron-down"></i></button>
        <button class="icon-btn" onclick="editAdminTopic(${i})" title="แก้ไข"><i class="ti ti-edit"></i></button>
        <button class="icon-btn danger" onclick="deleteAdminTopic(${i})" title="ลบ"><i class="ti ti-trash"></i></button>
      </div>
    </div>`).join('') : '<div class="empty">ยังไม่มีหัวข้อ</div>';
}

function renderAdminStepEditor(steps: ReturnType<typeof normalizeStep>[]): void {
  document.getElementById('adminStepEditor')!.innerHTML = steps.map((s, i) => `
    <div class="step-row">
      <div class="step-row-header">
        <div class="step-row-num">${i + 1}</div>
        <input type="text" class="step-name-inp" value="${esc(s.name)}" placeholder="ชื่อขั้นตอน">
        <button type="button" class="icon-btn danger" onclick="removeAdminStepRow(this)" ${steps.length <= 1 ? 'disabled' : ''}><i class="ti ti-minus"></i></button>
      </div>
      ${buildRichEditor(s.msg.text, s.msg.imageUrl, s.msg.links, 'ข้อความ default...')}
    </div>`).join('');
}

export function showAdminTopicForm(idx: number | null = null): void {
  set.adminEditingTopicIdx(idx);
  const isEdit = idx !== null;
  const t = isEdit ? topicsCache[idx]! : { name: '', steps: [{ name: '', msg: '' }], lineColor: '#1D4ED8' };
  (document.getElementById('adminTopicNameInp') as HTMLInputElement).value = t.name;
  const col = t.lineColor ?? '#1D4ED8';
  (document.getElementById('adminTopicColorInp') as HTMLInputElement).value = col;
  (document.getElementById('adminTopicColorHex') as HTMLInputElement).value = col;
  renderAdminStepEditor((t.steps ?? ['']).map(s => normalizeStep(s)));
  (document.getElementById('adminTopicForm') as HTMLElement).style.display = 'block';
  (document.getElementById('adminAddTopicBtn') as HTMLElement).style.display = 'none';
  (document.getElementById('adminTopicNameInp') as HTMLInputElement).focus();
}

export function editAdminTopic(i: number): void { showAdminTopicForm(i); }

export function cancelAdminTopicForm(): void {
  set.adminEditingTopicIdx(null);
  (document.getElementById('adminTopicForm') as HTMLElement).style.display = 'none';
  (document.getElementById('adminAddTopicBtn') as HTMLElement).style.display = 'flex';
}

export function addAdminStepRow(): void {
  const ed = document.getElementById('adminStepEditor')!;
  const num = ed.querySelectorAll('.step-row').length + 1;
  const div = document.createElement('div'); div.className = 'step-row';
  div.innerHTML = `<div class="step-row-header"><div class="step-row-num">${num}</div><input type="text" class="step-name-inp" placeholder="ชื่อขั้นตอน"><button type="button" class="icon-btn danger" onclick="removeAdminStepRow(this)"><i class="ti ti-minus"></i></button></div>${buildRichEditor('', '', [], 'ข้อความ default...')}`;
  ed.appendChild(div);
  (div.querySelector('input') as HTMLInputElement).focus();
  updateAdminRemoveBtns();
}

export function removeAdminStepRow(btn: HTMLElement): void {
  btn.closest('.step-row')!.remove();
  updateAdminRemoveBtns();
  document.querySelectorAll('#adminStepEditor .step-row-num').forEach((n, i) => { n.textContent = String(i + 1); });
}

function updateAdminRemoveBtns(): void {
  const rows = document.querySelectorAll('#adminStepEditor .step-row');
  rows.forEach(r => { (r.querySelector('.icon-btn') as HTMLButtonElement).disabled = rows.length <= 1; });
}

function getAdminSteps() {
  return [...document.querySelectorAll('#adminStepEditor .step-row')].map(r => ({
    name: (r.querySelector('.step-name-inp') as HTMLInputElement).value.trim(),
    msg: getRichData(r as HTMLElement),
  })).filter(s => s.name);
}

export function syncTopicColorFromHex(): void {
  const hex = (document.getElementById('adminTopicColorHex') as HTMLInputElement).value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) (document.getElementById('adminTopicColorInp') as HTMLInputElement).value = hex;
}

export async function submitAdminTopicForm(): Promise<void> {
  const name = (document.getElementById('adminTopicNameInp') as HTMLInputElement).value.trim();
  if (!name) { toast('warning', 'กรุณาใส่ชื่อหัวข้อ'); return; }
  const steps = getAdminSteps();
  if (!steps.length) { toast('warning', 'ต้องมีอย่างน้อย 1 ขั้นตอน'); return; }
  const lineColor = (document.getElementById('adminTopicColorInp') as HTMLInputElement).value || '#1D4ED8';
  if (adminEditingTopicIdx !== null) {
    topicsCache[adminEditingTopicIdx] = { ...topicsCache[adminEditingTopicIdx]!, name, steps, lineColor };
  } else {
    if (topicsCache.find(t => t.name === name)) { toast('warning', 'ชื่อหัวข้อซ้ำ'); return; }
    topicsCache.push({ name, steps, lineColor });
  }
  cancelAdminTopicForm(); renderAdminTopicList();
  localStorage.setItem('hr_topics', JSON.stringify(topicsCache));
  await saveTopicsToServer();
  await saveTopicColors();
}

async function saveTopicColors(): Promise<void> {
  const topicColors: Record<string, string> = {};
  topicsCache.forEach(t => { if (t.lineColor) topicColors[t.name] = t.lineColor; });
  const current = await callApi({ action: 'getLineConfig' });
  const cfg = current.ok ? { ...(current['config'] as Record<string, unknown>) } : {};
  cfg['topicColors'] = topicColors;
  await callApi({ action: 'saveLineConfig', config: cfg });
}

export async function deleteAdminTopic(i: number): Promise<void> {
  if (!confirm(`ลบหัวข้อ "${topicsCache[i]?.name}"?`)) return;
  topicsCache.splice(i, 1); renderAdminTopicList();
  localStorage.setItem('hr_topics', JSON.stringify(topicsCache));
  await saveTopicsToServer();
}

export async function moveAdminTopic(i: number, dir: number): Promise<void> {
  const j = i + dir;
  if (j < 0 || j >= topicsCache.length) return;
  [topicsCache[i], topicsCache[j]] = [topicsCache[j]!, topicsCache[i]!];
  renderAdminTopicList();
  localStorage.setItem('hr_topics', JSON.stringify(topicsCache));
  await saveTopicsToServer();
}

async function saveTopicsToServer(): Promise<void> {
  const data = await callApi({ action: 'saveTopics', topics: topicsCache });
  if (data.ok) toast('success', 'บันทึกหัวข้อแล้ว');
  else toast('error', data.error ?? 'บันทึกไม่สำเร็จ');
}

